import React, { useCallback, useMemo } from 'react'
import Autocomplete, { AutocompleteProps, AutocompleteRenderInputParams, AutocompleteRenderOptionState } from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import { observer } from 'mobx-react-lite'
import { AutocompleteGetTagProps } from '@mui/material/useAutocomplete'
import useStore from 'reusable/mobx-store-utils'
import { GapAutocompleteData, GapAutocompleteOption, GapAutocompleteOptionExtended } from '../types/GapAutocompleteTypes'
import { GapAutocompleteMultipleStore } from './GapAutocompleteMultipleStore'
import { gapAutoCompleteRenderInput, gapAutocompleteIsOptionEqualToValue, gapAutoCompleteRenderOption, gapAutocompleteNormaliseOptions, gapAutocompleteFilterOptions, gapAutocompleteHierarchyFilterOptions, gapAutocompleteGetOptionLabel } from '../shared/GapAutocompleteShared'
import { useTheme } from 'theme/useTheme'

export interface GapAutocompleteMultipleProps extends Omit<AutocompleteProps<GapAutocompleteOption, true, undefined, undefined>,
   | 'multiple'
   | 'freeSolo'
   | 'disableClearable'
   | 'onChange'
   | 'value'
   | 'renderInput'
   | 'getOptionLabel'
   | 'isOptionEqualToValue'
   | 'options'
   | 'defaultValue'
> {
   /**
    * A GapAutocompleteOption array or GapAutocompleteHierarchy with categories and optional subcategories.
   */
   options: GapAutocompleteData
   onChange?: (value?: any) => any
   onNewOption?: (inputValue: string[]) => void
   value?: string[]
   label: string
   helperText?: string
   error?: boolean
   /**
    * Displays the Check Icon right-aligned next to selected options.
    * @default true
    */
   displaySelectedIcon?: boolean
   /**
    * The maximum number of tags to display when multiple is true before displaying a "+X more" chip.
    * @default 2
    */
   maxTags?: number
   /**
    * Maximum length of the selected tag label before truncating and showing "..."
    * @default 30
    */
   maxTagLength?: number
   variant?: 'standard' | 'outlined' | 'filled'
}

export default observer(function GapAutocompleteMultiple(props: GapAutocompleteMultipleProps) {
   const theme = useTheme()
   const { options, value, onChange, onNewOption, helperText, error, label, disableCloseOnSelect, disabled, readOnly, variant, displaySelectedIcon = true, maxTags = 2, maxTagLength = 30 } = props
   const gapAutocompleteMultipleStore = useStore((sp) => new GapAutocompleteMultipleStore(sp), { options, value, onChange, onNewOption })

   const normalisedOptions = useMemo(() => {
      return gapAutocompleteNormaliseOptions(options)
   }, [options])

   const selectedOptions = useMemo(() => {
      const valueArray = Array.isArray(props.value) ? props.value : []
      return normalisedOptions.filter(option => valueArray.includes(option.id))
   }, [props.value, normalisedOptions])

   const renderInput = useCallback(
      (params: AutocompleteRenderInputParams) => gapAutoCompleteRenderInput({ label, helperText, error, readOnly, variant })(params),
      [helperText, error, label, readOnly, variant]
   )

   const renderOption = useCallback(
      (props: React.HTMLAttributes<HTMLLIElement>, option: GapAutocompleteOptionExtended, state: AutocompleteRenderOptionState) =>
         gapAutoCompleteRenderOption(displaySelectedIcon, theme.palette.gap.primary['200'])(props, option, state),
      [displaySelectedIcon, theme]
   )

   const renderTags = useCallback(
      (tagValue: GapAutocompleteOption[], getTagProps: AutocompleteGetTagProps) => {
         // Calculate the number of tags to display based on maxTags
         // If maxTags is undefined, display all tags
         const displayedTags = maxTags ? tagValue.slice(0, maxTags) : tagValue
         const additionalTagsCount = maxTags ? tagValue.length - maxTags : 0

         return (
            <>
               {displayedTags.map((option, index) => (
                  <Chip
                     {...getTagProps({ index })}
                     // Truncate the label if maxTagLength is defined and the label exceeds this length
                     label={
                        maxTagLength && option.label.length > maxTagLength
                           ? `${option.label.slice(0, maxTagLength)}...`
                           : option.label
                     }
                     key={option.id}
                  />
               ))}
               {/* If there are more tags than maxTags, display a "+X more" chip. */}
               {additionalTagsCount > 0 && <Chip label={`+${additionalTagsCount} more`} />}
            </>
         )
      },
      [maxTags, maxTagLength]
   )

   return (
      <Autocomplete
         value={selectedOptions}
         options={normalisedOptions}
         multiple
         renderInput={renderInput}
         renderTags={renderTags}
         renderOption={renderOption}
         disableCloseOnSelect={!!disableCloseOnSelect}
         disabled={disabled}
         isOptionEqualToValue={gapAutocompleteIsOptionEqualToValue}
         onChange={gapAutocompleteMultipleStore.onChange}
         getOptionLabel={gapAutocompleteGetOptionLabel}
         freeSolo={!!onNewOption}
         filterOptions={!!onNewOption ? gapAutocompleteFilterOptions : gapAutocompleteHierarchyFilterOptions}
      />
   )
})
